import { TETO_BUSCA } from '@malgoos/shared';

/**
 * Filtros de tela gravados nas visões salvas da Tabela (review r3 spec): a visão leva a busca e
 * todos os filtros extras da tela (selects, período, chaves), não só a busca.
 */

import i18n from 'i18next';
import { ErroRequisicao, mensagemDeErro } from '../nucleo/api';

/** Valores dos filtros extras que a tela mostra junto da Tabela. */
export type FiltrosTabela = Record<string, string | boolean>;

/** O que vai em `filtros` ao salvar a visão: os filtros extras da tela mais a busca. */
export function filtrosParaVisao(busca: string, filtros?: FiltrosTabela): Record<string, unknown> {
  return { ...(filtros ?? {}), busca };
}

/**
 * Filtros extras a restaurar quando a visão é aplicada. Só entram as chaves que a tela conhece,
 * com o mesmo tipo do valor inicial; o que a visão não traz (visão antiga, chave nova) volta ao
 * valor inicial da tela.
 */
export function filtrosDaVisao(salvos: Record<string, unknown> | null | undefined, iniciais: FiltrosTabela): FiltrosTabela {
  const saida: FiltrosTabela = { ...iniciais };
  for (const [chave, inicial] of Object.entries(iniciais)) {
    const v = salvos?.[chave];
    if (typeof v === typeof inicial) saida[chave] = v as string | boolean;
  }
  return saida;
}

/**
 * Review r25 spec (M1): grupos de filtros que andam juntos. Um link que traz uma parte do grupo (como `?de=&ate=` sem
 * `campo_data`, do relatório de Margem) leva o grupo inteiro: a base da visão padrão não troca o sentido das datas do link.
 */
export const GRUPOS_FILTROS: readonly (readonly string[])[] = [
  ['campo_data', 'de', 'ate'],
  ['valor_min_centavos', 'valor_max_centavos'],
];

/**
 * Review r25 spec (M1): filtros da visão PADRÃO aplicada sozinha ao abrir a lista. Os filtros que chegaram preenchidos
 * na URL (o link do cliente, do aviso, do relatório) prevalecem sobre os da visão; valor vazio da visão nunca apaga
 * valor do link. Só conta como "do link" a chave da tela que está preenchida na URL e na tela (a tela com filtro em
 * estado local não é afetada pelo valor padrão dela). Escolher a visão no seletor continua trocando tudo
 * (`filtrosDaVisao` sozinho).
 */
export function filtrosDaVisaoPadrao(salvos: Record<string, unknown> | null | undefined, atuais: FiltrosTabela, url: URLSearchParams): FiltrosTabela {
  const saida = filtrosDaVisao(salvos, atuais);
  const doLink = (chave: string) => chave in atuais && !!url.get(chave) && atuais[chave] !== '' && atuais[chave] !== false;
  for (const chave of Object.keys(atuais)) if (doLink(chave)) saida[chave] = atuais[chave]!;
  for (const grupo of GRUPOS_FILTROS) {
    if (!grupo.some(doLink)) continue;
    for (const chave of grupo) if (chave in atuais) saida[chave] = atuais[chave]!;
  }
  return saida;
}

/** Para telas que guardam os filtros na URL: grava cada filtro como parâmetro (vazio ou falso remove). */
export function filtrosNaUrl(atual: URLSearchParams, filtros: FiltrosTabela): URLSearchParams {
  const n = new URLSearchParams(atual);
  for (const [chave, v] of Object.entries(filtros)) {
    if (v === '' || v === false) n.delete(chave);
    else n.set(chave, String(v));
  }
  return n;
}

/**
 * Review r24 spec (B4): busca da Tabela na URL. Os avisos do Contas (contrato da W22) abrem a lista com `?busca=<número>`;
 * a busca inicial vem daqui e cada mudança volta para a URL, então o link filtra e a URL mostra o que está na tela.
 * Review r28 robustez (B2): cortada no teto da API (`TETO_BUSCA`, 200, o mesmo da visão e do campo): antes, o link com 201
 * caracteres ou mais dava 422 e a lista "Indisponível". O corte não deixa meia letra (surrogate) no fim.
 */
export function buscaDaUrl(url: URLSearchParams): string {
  const v = url.get('busca') ?? '';
  if (v.length <= TETO_BUSCA) return v;
  const cortada = v.slice(0, TETO_BUSCA);
  return /[\uD800-\uDBFF]$/.test(cortada) ? cortada.slice(0, -1) : cortada;
}

/** Grava a busca na URL sem mexer nos outros parâmetros; vazia (ou só espaço) tira o parâmetro. */
export function comBuscaNaUrl(atual: URLSearchParams, busca: string): URLSearchParams {
  const n = new URLSearchParams(atual);
  if (busca.trim()) n.set('busca', busca);
  else n.delete('busca');
  return n;
}

/**
 * Review r26 robustez (M2): teto do que a visão pode levar para a URL. A tela grava os filtros na URL e a busca entra
 * por cima: uma visão (antiga, de antes do teto da API) com um valor de 500 KB virava URL de 502 KB e a lista dava 431 em
 * toda abertura. 8.000 caracteres a mais cabem em qualquer navegador e, com a URL do link, no limite de cabeçalho do
 * servidor (16 KB; o Node responde 431 acima dele).
 */
export const TETO_URL_VISAO = 8_000;
/** O mesmo teto da API para o JSON dos filtros, com folga (a API conta bytes do JSON compacto). */
export const TETO_JSON_VISAO = 32 * 1024;
/**
 * Review r27 robustez (O2): o maior texto que um filtro de lista aceita na API (`MAX_TEXTO_IDS_FILTRO` do kernel, a lista de
 * ids separados por vírgula); os outros filtros (datas, estados, valores) são menores. A busca tem o dela (`TETO_BUSCA`).
 */
export const TETO_TEXTO_FILTRO = 4096;

/**
 * A visão pode ser aplicada sem travar a tela: a API não a marcou acima do teto, o JSON dos filtros cabe e o que ela grava
 * na URL (filtros da tela mais a busca, por cima da URL atual) não passa de `TETO_URL_VISAO` a mais. Senão, a Tabela
 * ignora a visão e avisa.
 */
export function visaoCabe(v: { filtros?: Record<string, unknown> | null; acima_do_teto?: boolean }, iniciais: FiltrosTabela, urlAtual: URLSearchParams = new URLSearchParams()): boolean {
  if (v.acima_do_teto) return false;
  let json = '';
  try {
    json = JSON.stringify(v.filtros ?? {});
  } catch {
    return false;
  }
  if (json.length > TETO_JSON_VISAO) return false;
  const busca = typeof v.filtros?.busca === 'string' ? v.filtros.busca : '';
  // Review r27 robustez (O2): cada valor que a visão aplica tem de caber no teto da API da lista; senão a lista dá 422 em
  // toda abertura (a visão padrão salva pela API com uma busca de 4.000 caracteres).
  if (busca.length > TETO_BUSCA) return false;
  for (const valor of Object.values(filtrosDaVisao(v.filtros, iniciais))) if (typeof valor === 'string' && valor.length > TETO_TEXTO_FILTRO) return false;
  const depois = comBuscaNaUrl(filtrosNaUrl(urlAtual, filtrosDaVisao(v.filtros, iniciais)), busca).toString().length;
  return depois - urlAtual.toString().length <= TETO_URL_VISAO;
}

/**
 * Review r27 spec (M1): texto do erro ao salvar ou remover uma visão. O teto de tamanho (16 KB no JSON dos filtros, 4.096
 * caracteres por valor, review r26 robustez M2) volta da API como 422 `entrada.invalida` com o campo `filtros...` (ou
 * `ordenacao`, `colunas`): sem campo para marcar no diálogo, o "Valor inválido" genérico não dizia nada, então vira o texto
 * próprio. `visao.limite`, `visao.padrao_conflito` e os demais seguem a tradução do código (`mensagemDeErro`).
 * Review r28 spec (O4): o limite por pessoa (200, `escopo: 'pessoa'` nos detalhes) tem texto próprio nos três idiomas, que
 * diz "em qualquer tela"; antes, en e es usavam o texto do limite da tela e a pessoa procurava a visão a remover só ali.
 */
export function mensagemDoErroDaVisao(e: unknown): string {
  if (e instanceof ErroRequisicao && e.status === 422 && e.corpo.code === 'entrada.invalida' && Array.isArray(e.corpo.details)) {
    const doTamanho = (e.corpo.details as { campo?: unknown }[]).some((d) => typeof d?.campo === 'string' && /^(filtros|ordenacao|colunas)(\.|$)/.test(d.campo));
    if (doTamanho) return i18n.t('tabela.visaoGrande');
  }
  if (e instanceof ErroRequisicao && e.corpo.code === 'visao.limite') {
    const meta = e.metaDoErro();
    if (meta?.escopo === 'pessoa') return i18n.t('tabela.visaoLimitePessoa', { max: typeof meta.max === 'number' ? meta.max : 200 });
  }
  return mensagemDeErro(e);
}
